import React, { useState, useEffect } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid': return '#10b981';
    case 'partial': return '#f59e0b';
    case 'pending': return '#ef4444';
    default: return '#6b7280';
  }
};

const getStatusBgColor = (status: string) => {
  switch (status) {
    case 'paid': return '#ecfdf5';
    case 'partial': return '#fffbeb';
    case 'pending': return '#fef2f2';
    default: return '#f9fafb';
  }
};

interface DistributorHistoryTableProps {
  filteredDistributors: any[];
  distributorsData: any[];
  handleRequestStock: (distributor: any) => void;
}

const DistributorHistoryTable: React.FC<DistributorHistoryTableProps> = ({
  filteredDistributors,
  distributorsData,
  handleRequestStock
}) => {
  // State for selected distributor and purchases
  const [selectedDistributor, setSelectedDistributor] = useState<number | null>(null);
  const [distributorPurchases, setDistributorPurchases] = useState<any[]>([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [purchasesError, setPurchasesError] = useState('');
  const [purchasesPagination, setPurchasesPagination] = useState<any>(null);
  
  // State for expanded purchase medications
  const [expandedPurchaseId, setExpandedPurchaseId] = useState<number | null>(null);
  const [purchaseMedications, setPurchaseMedications] = useState<any>(null);
  const [isLoadingMedications, setIsLoadingMedications] = useState(false);
  const [medicationsError, setMedicationsError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch distributor purchases
  const fetchDistributorPurchases = async (distributorId: number) => {
    try {
      setIsLoadingPurchases(true);
      setPurchasesError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        console.warn('⚠️ No auth token found in localStorage. API call may fail.');
        setPurchasesError('Authentication token not found. Please log in again.');
        setIsLoadingPurchases(false);
        return;
      }
      
      console.log(`Fetching purchases for distributor ID: ${distributorId}`);
      console.log('Using token:', authToken ? 'Yes' : 'No');
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/distributor/${distributorId}`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle paginated response structure
      if (data && data.purchases && Array.isArray(data.purchases)) {
        setDistributorPurchases(data.purchases);
        setPurchasesPagination({
          current_page: data.current_page,
          pages: data.pages,
          per_page: data.per_page,
          total: data.total
        });
      } else if (Array.isArray(data)) {
        // Fallback for direct array response
        setDistributorPurchases(data);
        setPurchasesPagination(null);
      } else {
        setDistributorPurchases([]);
        setPurchasesPagination(null);
      }
    } catch (error: any) {
      // Check for different types of errors
      if (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
          error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
          error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
          error.message.includes('certificate')) {
        setPurchasesError('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setPurchasesError(`
          CORS Error: Unable to connect to the purchases API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure the API endpoint is accessible
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/distributor/{id}
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setPurchasesError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setPurchasesError('Access denied. You do not have permission to view purchases data.');
      } else {
        setPurchasesError(`Failed to load purchases data: ${error.message}`);
      }
      
      // Set empty array on error
      setDistributorPurchases([]);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  // Fetch purchase medications
  const fetchPurchaseMedications = async (purchaseId: number) => {
    try {
      setIsLoadingMedications(true);
      setMedicationsError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        setMedicationsError('Authentication token not found. Please log in again.');
        setIsLoadingMedications(false);
        return;
      }
      
      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/info`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setPurchaseMedications(data);
    } catch (error: any) {
      // Check for different types of errors
      if (error.message.includes('ERR_CERT_COMMON_NAME_INVALID') || 
          error.message.includes('ERR_CERT_AUTHORITY_INVALID') ||
          error.message.includes('ERR_SSL_PROTOCOL_ERROR') ||
          error.message.includes('certificate')) {
        setMedicationsError('SSL Certificate Error: The server certificate is invalid. Please contact the administrator or try using HTTP instead of HTTPS.');
      } else if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('preflight request') || error.message.includes('ERR_FAILED')) {
        setMedicationsError(`
          CORS Error: Unable to connect to the medications API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure the API endpoint is accessible
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/{id}/info
        `);
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setMedicationsError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setMedicationsError('Access denied. You do not have permission to view medications data.');
      } else {
        setMedicationsError(`Failed to load medications data: ${error.message}`);
      }
      
      setPurchaseMedications(null);
    } finally {
      setIsLoadingMedications(false);
    }
  };

  // Handle view medications button click
  const handleViewMedications = (purchaseId: number) => {
    if (expandedPurchaseId === purchaseId) {
      // If already expanded, hide the medications
      setExpandedPurchaseId(null);
      setPurchaseMedications(null);
      setMedicationsError('');
    } else {
      // Expand and fetch medications
      setExpandedPurchaseId(purchaseId);
      fetchPurchaseMedications(purchaseId);
    }
  };

  // Handle view button click
  const handleViewDistributor = (distributorId: number) => {
    if (selectedDistributor === distributorId) {
      // If already selected, hide the details
      setSelectedDistributor(null);
      setDistributorPurchases([]);
      setPurchasesError('');
      setPurchasesPagination(null);
      setExpandedPurchaseId(null);
      setPurchaseMedications(null);
      setMedicationsError('');
    } else {
      // Select new distributor and fetch purchases
      setSelectedDistributor(distributorId);
      fetchDistributorPurchases(distributorId);
    }
  };
  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
      backdropFilter: 'blur(25px)',
      borderRadius: isMobile ? '0' : '20px',
      padding: isMobile ? '0.75rem' : '1.5rem',
      border: isMobile ? 'none' : '1px solid rgba(16, 185, 129, 0.1)',
      boxShadow: isMobile ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.08)',
      overflowX: isMobile ? 'hidden' : 'auto',
      boxSizing: 'border-box',
      margin: isMobile ? '0' : '0'
    }}>
      {isMobile ? (
        // Mobile Card Layout
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {(filteredDistributors.length > 0 ? filteredDistributors : distributorsData).map((distributor, index) => (
            <div key={distributor.id} style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1rem',
              border: '1px solid rgba(226, 232, 240, 0.5)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s ease'
            }}>
              {/* Mobile Card Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
              }}>
                <div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    ID
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#374151'
                  }}>
                    #{distributor.id || 'N/A'}
                  </div>
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                  background: getStatusBgColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                                distributor.settled_amount > 0 ? 'partial' : 'pending'),
                  color: getStatusColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                         distributor.settled_amount > 0 ? 'partial' : 'pending'),
                  border: `1px solid ${getStatusColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                                       distributor.settled_amount > 0 ? 'partial' : 'pending')}20`
                }}>
                  {distributor.unsettled_amount === 0 ? 'paid' : 
                   distributor.settled_amount > 0 ? 'partial' : 'pending'}
                </div>
              </div>

              {/* Name Section */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  fontWeight: '600',
                  marginBottom: '0.25rem'
                }}>
                  Name
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '0.25rem'
                }}>
                  {distributor.name || 'Unknown Distributor'}
                </div>
                {distributor.address && (
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                    fontWeight: '400'
                  }}>
                    {distributor.address}
                  </div>
                )}
              </div>

              {/* Contact Section */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  fontWeight: '600',
                  marginBottom: '0.25rem'
                }}>
                  Contact
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}>
                  {distributor.phone || 'N/A'}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: '#9ca3af'
                }}>
                  {distributor.email || 'N/A'}
                </div>
                {distributor.gstin && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#6b7280',
                    fontFamily: 'monospace',
                    marginTop: '0.25rem'
                  }}>
                    GSTIN: {distributor.gstin}
                  </div>
                )}
              </div>

              {/* Amounts Section */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '0.5rem',
                marginBottom: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(248, 250, 252, 0.5)',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#9ca3af',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    Total
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: '#3b82f6'
                  }}>
                    ₹{((distributor.settled_amount || 0) + (distributor.unsettled_amount || 0)).toFixed(2)}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#9ca3af',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    Paid
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: '#10b981'
                  }}>
                    ₹{(distributor.settled_amount || 0).toFixed(2)}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#9ca3af',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    Pending
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: '#ef4444'
                  }}>
                    ₹{(distributor.unsettled_amount || 0).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexDirection: 'column'
              }}>
                <button
                  onClick={() => handleViewDistributor(distributor.id)}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
                    width: '100%'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  {selectedDistributor === distributor.id ? 'Hide Details' : 'View Details'}
                </button>
                
                <button
                  onClick={() => handleRequestStock(distributor)}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)',
                    width: '100%'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  </svg>
                  Request Stock
                </button>
              </div>

              {/* Expanded Details for Mobile */}
              {selectedDistributor === distributor.id && (
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '2px solid rgba(16, 185, 129, 0.2)'
                }}>
                  {/* Purchase History Section */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#10b981' }}>
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                    </svg>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#1f2937',
                      margin: 0
                    }}>
                      Purchase History
                    </h4>
                  </div>

                  {/* Loading State for Purchases */}
                  {isLoadingPurchases && (
                    <div style={{
                      textAlign: 'center',
                      padding: '1.5rem',
                      color: '#6b7280'
                    }}>
                      <div style={{
                        width: '30px',
                        height: '30px',
                        border: '3px solid #e5e7eb',
                        borderTop: '3px solid #10b981',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 1rem auto'
                      }}></div>
                      <p style={{
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        margin: 0
                      }}>
                        Loading purchase history...
                      </p>
                    </div>
                  )}

                  {/* Error State for Purchases */}
                  {purchasesError && !isLoadingPurchases && (
                    <div style={{
                      textAlign: 'center',
                      padding: '1.5rem',
                      color: '#6b7280'
                    }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      <p style={{
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        margin: 0
                      }}>
                        {purchasesError}
                      </p>
                    </div>
                  )}

                  {/* Purchases List - Mobile */}
                  {!isLoadingPurchases && !purchasesError && distributorPurchases.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      {distributorPurchases.map((purchase, purchaseIndex) => (
                        <div key={purchase.id || purchaseIndex} style={{
                          background: 'rgba(248, 250, 252, 0.5)',
                          padding: '1rem',
                          borderRadius: '10px',
                          border: '1px solid rgba(226, 232, 240, 0.5)',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                          {/* Mobile Purchase Card Header */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.75rem',
                            paddingBottom: '0.75rem',
                            borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                          }}>
                            <div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                                fontWeight: '600',
                                marginBottom: '0.25rem'
                              }}>
                                Invoice
                              </div>
                              <div style={{
                                fontSize: '0.9rem',
                                fontWeight: '700',
                                color: '#1f2937'
                              }}>
                                #{purchase.invoice_no || purchase.id}
                              </div>
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              fontWeight: '500'
                            }}>
                              {purchase.invoice_date || 'N/A'}
                            </div>
                          </div>

                          {/* Purchase Details - Mobile */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            marginBottom: '0.75rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.5rem',
                              background: 'rgba(59, 130, 246, 0.05)',
                              borderRadius: '6px'
                            }}>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: '600'
                              }}>
                                Purchase ID
                              </span>
                              <span style={{
                                fontSize: '0.85rem',
                                color: '#3b82f6',
                                fontWeight: '700',
                                fontFamily: 'monospace'
                              }}>
                                #{purchase.id}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                                fontWeight: '600'
                              }}>
                                Total Amount
                              </span>
                              <span style={{
                                fontSize: '0.9rem',
                                fontWeight: '700',
                                color: '#1f2937'
                              }}>
                                ₹{purchase.total_amount?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                                fontWeight: '600'
                              }}>
                                Payment Mode
                              </span>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#6b7280',
                                textTransform: 'capitalize'
                              }}>
                                {purchase.payment_mode || 'N/A'}
                              </span>
                            </div>
                          </div>

                          {/* Status and Amounts - Mobile */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid rgba(226, 232, 240, 0.5)'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px',
                                backgroundColor: purchase.status === 'received' ? '#ecfdf5' : 
                                                purchase.status === 'pending' ? '#fef2f2' : 
                                                purchase.status === 'paid' ? '#ecfdf5' : '#fffbeb',
                                color: purchase.status === 'received' ? '#10b981' : 
                                       purchase.status === 'pending' ? '#ef4444' : 
                                       purchase.status === 'paid' ? '#10b981' : '#f59e0b',
                                fontWeight: '600',
                                textTransform: 'capitalize'
                              }}>
                                {purchase.status || 'Unknown'}
                              </span>
                              <div style={{
                                display: 'flex',
                                gap: '0.5rem',
                                flexDirection: 'column',
                                alignItems: 'flex-end'
                              }}>
                                {purchase.settled_amount > 0 && (
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#10b981',
                                    fontWeight: '600'
                                  }}>
                                    Paid: ₹{purchase.settled_amount.toFixed(2)}
                                  </div>
                                )}
                                {purchase.unsettled_amount > 0 && (
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#ef4444',
                                    fontWeight: '600'
                                  }}>
                                    Due: ₹{purchase.unsettled_amount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* View Medications Button - Mobile */}
                          <div style={{
                            marginTop: '0.75rem'
                          }}>
                            <button
                              onClick={() => handleViewMedications(purchase.id)}
                              style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.4rem',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                                width: '100%'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"/>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                              </svg>
                              {expandedPurchaseId === purchase.id ? 'Hide Medications' : 'View Medications'}
                            </button>
                          </div>

                          {/* Created Date - Mobile */}
                          <div style={{
                            fontSize: '0.65rem',
                            color: '#9ca3af',
                            marginTop: '0.5rem',
                            textAlign: 'right'
                          }}>
                            Created: {new Date(purchase.created_at).toLocaleDateString()} {new Date(purchase.created_at).toLocaleTimeString()}
                          </div>

                          {/* Medications Section - Mobile */}
                          {expandedPurchaseId === purchase.id && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '1rem',
                              background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
                              borderRadius: '8px',
                              border: '1px solid rgba(59, 130, 246, 0.1)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '1rem'
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                  <path d="M9 12l2 2 4-4"/>
                                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                </svg>
                                <h5 style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '700',
                                  color: '#1f2937',
                                  margin: 0
                                }}>
                                  Medications Details
                                </h5>
                              </div>

                              {/* Loading State for Medications */}
                              {isLoadingMedications && (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '1rem',
                                  color: '#6b7280'
                                }}>
                                  <div style={{
                                    width: '25px',
                                    height: '25px',
                                    border: '2px solid #e5e7eb',
                                    borderTop: '2px solid #3b82f6',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto 0.75rem auto'
                                  }}></div>
                                  <p style={{
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                    margin: 0
                                  }}>
                                    Loading medications...
                                  </p>
                                </div>
                              )}

                              {/* Error State for Medications */}
                              {medicationsError && !isLoadingMedications && (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '1rem',
                                  color: '#6b7280'
                                }}>
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.5rem', opacity: 0.5 }}>
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="15" y1="9" x2="9" y2="15"/>
                                    <line x1="9" y1="9" x2="15" y2="15"/>
                                  </svg>
                                  <p style={{
                                    fontSize: '0.8rem',
                                    color: '#6b7280',
                                    margin: 0
                                  }}>
                                    {medicationsError}
                                  </p>
                                </div>
                              )}

                              {/* Medications Data Display - Mobile */}
                              {!isLoadingMedications && !medicationsError && purchaseMedications && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '0.75rem'
                                }}>
                                  {/* Purchase Summary - Mobile */}
                                  <div style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(59, 130, 246, 0.1)'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.5rem',
                                      fontSize: '0.8rem'
                                    }}>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Purchase ID: </span>
                                        <span style={{ fontWeight: '700', color: '#3b82f6' }}>#{purchaseMedications.id}</span>
                                      </div>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Invoice: </span>
                                        <span style={{ fontWeight: '500', color: '#374151' }}>{purchaseMedications.invoice_no}</span>
                                      </div>
                                      <div>
                                        <span style={{ fontWeight: '600', color: '#6b7280' }}>Date: </span>
                                        <span style={{ fontWeight: '500', color: '#374151' }}>{purchaseMedications.invoice_date}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Medications List - Mobile */}
                                  {purchaseMedications.items && purchaseMedications.items.length > 0 && (
                                    <div>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        marginBottom: '0.75rem'
                                      }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                          <path d="M9 12l2 2 4-4"/>
                                          <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                        </svg>
                                        <h6 style={{
                                          fontSize: '0.85rem',
                                          fontWeight: '700',
                                          color: '#1f2937',
                                          margin: 0
                                        }}>
                                          Medications ({purchaseMedications.items.length} items)
                                        </h6>
                                      </div>
                                      
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem'
                                      }}>
                                        {purchaseMedications.items.map((item: any, itemIndex: number) => (
                                          <div key={itemIndex} style={{
                                            background: 'white',
                                            padding: '0.75rem',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(226, 232, 240, 0.5)',
                                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                                            position: 'relative'
                                          }}>
                                            {/* Item Number Badge */}
                                            <div style={{
                                              position: 'absolute',
                                              top: '0.5rem',
                                              right: '0.5rem',
                                              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                              color: 'white',
                                              padding: '0.2rem 0.4rem',
                                              borderRadius: '8px',
                                              fontSize: '0.65rem',
                                              fontWeight: '600',
                                              fontFamily: 'monospace'
                                            }}>
                                              #{itemIndex + 1}
                                            </div>

                                            {/* Medicine Name */}
                                            <div style={{
                                              fontSize: '0.9rem',
                                              fontWeight: '700',
                                              color: '#1f2937',
                                              marginBottom: '0.75rem',
                                              paddingRight: '2.5rem'
                                            }}>
                                              {item.medicine_name || 'Unknown Medicine'}
                                            </div>

                                            {/* Medicine Details - Mobile */}
                                            <div style={{
                                              display: 'grid',
                                              gridTemplateColumns: '1fr 1fr',
                                              gap: '0.5rem',
                                              marginBottom: '0.75rem'
                                            }}>
                                              <div style={{
                                                background: 'rgba(16, 185, 129, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(16, 185, 129, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>QUANTITY</div>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</div>
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(59, 130, 246, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(59, 130, 246, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>COST PRICE</div>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#3b82f6' }}>₹{item.cost_price_per_unit || '0.00'}</div>
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(245, 158, 11, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(245, 158, 11, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>MRP</div>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#f59e0b' }}>₹{item.mrp || '0.00'}</div>
                                              </div>
                                              
                                              <div style={{
                                                background: 'rgba(239, 68, 68, 0.05)',
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(239, 68, 68, 0.1)'
                                              }}>
                                                <div style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>TOTAL</div>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#ef4444' }}>₹{item.line_total || '0.00'}</div>
                                              </div>
                                            </div>

                                            {/* Additional Details - Mobile */}
                                            <div style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: '0.4rem',
                                              fontSize: '0.75rem',
                                              color: '#6b7280'
                                            }}>
                                              <div><strong>Batch:</strong> {item.batch_no || 'N/A'}</div>
                                              <div><strong>Expiry:</strong> {item.expiry_date || 'N/A'}</div>
                                              <div><strong>Tax:</strong> {item.tax_amount || '0.00'}%</div>
                                              <div><strong>Company:</strong> {item.medicine_company || 'N/A'}</div>
                                              <div><strong>Composition:</strong> {item.medicine_composition || 'N/A'}</div>
                                              <div><strong>Location:</strong> {item.storage_location || 'N/A'}</div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination Info - Mobile */}
                  {!isLoadingPurchases && !purchasesError && purchasesPagination && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(16, 185, 129, 0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.1)',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: '500'
                      }}>
                        Showing {distributorPurchases.length} of {purchasesPagination.total} purchases
                        {purchasesPagination.pages > 1 && (
                          <span> • Page {purchasesPagination.current_page} of {purchasesPagination.pages}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* No Purchases Found - Mobile */}
                  {!isLoadingPurchases && !purchasesError && distributorPurchases.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '1.5rem',
                      color: '#6b7280'
                    }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                      </svg>
                      <p style={{
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        margin: 0
                      }}>
                        No purchase history found for this distributor
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Desktop Table Layout
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '0',
          minWidth: '1200px'
        }}>
        {/* Table Header */}
        <thead>
          <tr style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
            borderBottom: '2px solid rgba(16, 185, 129, 0.2)'
          }}>
            <th style={{
              padding: '1rem',
              textAlign: 'left',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: '2px solid rgba(16, 185, 129, 0.2)'
            }}>ID</th>
            <th style={{
              padding: '1rem',
              textAlign: 'left',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Name</th>
            <th style={{
              padding: '1rem',
              textAlign: 'left',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Contact</th>
            <th style={{
              padding: '1rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Status</th>
            <th style={{
              padding: '1rem',
              textAlign: 'right',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Total Amount</th>
            <th style={{
              padding: '1rem',
              textAlign: 'right',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Paid Amount</th>
            <th style={{
              padding: '1rem',
              textAlign: 'right',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Pending Amount</th>
            <th style={{
              padding: '1rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: '700',
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>Actions</th>
          </tr>
        </thead>
        
        {/* Table Body */}
        <tbody>
          {/* Show filtered distributors, or all distributors if filtered is empty */}
          {(filteredDistributors.length > 0 ? filteredDistributors : distributorsData).map((distributor, index) => (
            <React.Fragment key={distributor.id}>
              <tr style={{
                background: index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)';
              }}
              >
                {/* ID */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  #{distributor.id || 'N/A'}
                </td>
                
                {/* Name */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  <div>
                    <div style={{ marginBottom: '0.25rem' }}>
                      {distributor.name || 'Unknown Distributor'}
                    </div>
                    {distributor.address && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#9ca3af',
                        fontWeight: '400'
                      }}>
                        {distributor.address}
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Contact */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  <div>
                    <div style={{ marginBottom: '0.25rem' }}>
                      {distributor.phone || 'N/A'}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#9ca3af'
                    }}>
                      {distributor.email || 'N/A'}
                    </div>
                    {distributor.gstin && (
                      <div style={{
                        fontSize: '0.7rem',
                        color: '#6b7280',
                        fontFamily: 'monospace',
                        marginTop: '0.25rem'
                      }}>
                        GSTIN: {distributor.gstin}
                      </div>
                    )}
                  </div>
                </td>
                
                {/* Status */}
                <td style={{
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    gap: '0.25rem',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      background: getStatusBgColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                                    distributor.settled_amount > 0 ? 'partial' : 'pending'),
                      color: getStatusColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                           distributor.settled_amount > 0 ? 'partial' : 'pending'),
                      border: `1px solid ${getStatusColor(distributor.unsettled_amount === 0 ? 'paid' : 
                                                           distributor.settled_amount > 0 ? 'partial' : 'pending')}20`
                    }}>
                      {distributor.unsettled_amount === 0 ? 'paid' : 
                       distributor.settled_amount > 0 ? 'partial' : 'pending'}
                    </span>
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#6b7280',
                      fontWeight: '500'
                    }}>
                      {(distributor.is_active !== undefined) ? (distributor.is_active ? 'Active' : 'Inactive') : 'Unknown'}
                    </div>
                  </div>
                </td>
                
                {/* Total Amount */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#3b82f6',
                  textAlign: 'right'
                }}>
                  ₹{((distributor.settled_amount || 0) + (distributor.unsettled_amount || 0)).toFixed(2)}
                </td>
                
                {/* Paid Amount */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#10b981',
                  textAlign: 'right'
                }}>
                  ₹{(distributor.settled_amount || 0).toFixed(2)}
                </td>
                
                {/* Pending Amount */}
                <td style={{
                  padding: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#ef4444',
                  textAlign: 'right'
                }}>
                  ₹{(distributor.unsettled_amount || 0).toFixed(2)}
                </td>
                
                {/* Actions */}
                <td style={{
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => handleViewDistributor(distributor.id)}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.2)';
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      {selectedDistributor === distributor.id ? 'Hide' : 'View'}
                    </button>
                    
                    <button
                      onClick={() => handleRequestStock(distributor)}
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
                        }
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                      </svg>
                      Request
                    </button>
                  </div>
                </td>
              </tr>
              
              {/* Expanded Details Row */}
              {selectedDistributor === distributor.id && (
                <tr>
                  <td colSpan={8} style={{
                    padding: '0',
                    background: 'rgba(248, 250, 252, 0.8)',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.5)'
                  }}>
                    <div style={{
                      padding: '1.5rem'
                    }}>
                      {/* Purchase History Section */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#10b981' }}>
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                        </svg>
                        <h4 style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#1f2937',
                          margin: 0
                        }}>
                          Purchase History
                        </h4>
                      </div>

                      {/* Loading State for Purchases */}
                      {isLoadingPurchases && (
                        <div style={{
                          textAlign: 'center',
                          padding: '2rem',
                          color: '#6b7280'
                        }}>
                          <div style={{
                            width: '30px',
                            height: '30px',
                            border: '3px solid #e5e7eb',
                            borderTop: '3px solid #10b981',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 1rem auto'
                          }}></div>
                          <p style={{
                            fontSize: '0.9rem',
                            color: '#6b7280',
                            margin: 0
                          }}>
                            Loading purchase history...
                          </p>
                        </div>
                      )}

                      {/* Error State for Purchases */}
                      {purchasesError && !isLoadingPurchases && (
                        <div style={{
                          textAlign: 'center',
                          padding: '2rem',
                          color: '#6b7280'
                        }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1rem', opacity: 0.5 }}>
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                          </svg>
                          <p style={{
                            fontSize: '0.9rem',
                            color: '#6b7280',
                            margin: 0
                          }}>
                            {purchasesError}
                          </p>
                        </div>
                      )}

                      {/* Purchases List */}
                      {!isLoadingPurchases && !purchasesError && distributorPurchases.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.75rem'
                        }}>
                          {distributorPurchases.map((purchase, purchaseIndex) => (
                            <div key={purchase.id || purchaseIndex} style={{
                              background: 'white',
                              padding: '1rem',
                              borderRadius: '8px',
                              border: '1px solid rgba(226, 232, 240, 0.5)',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                            }}>
                              {/* Header Row */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.75rem'
                              }}>
                                <div style={{
                                  fontSize: '0.9rem',
                                  fontWeight: '700',
                                  color: '#1f2937'
                                }}>
                                  Invoice #{purchase.invoice_no || purchase.id}
                                </div>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#6b7280',
                                  fontWeight: '500'
                                }}>
                                  {purchase.invoice_date || 'N/A'}
                                </div>
                              </div>

                              {/* Purchase ID Row */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.75rem',
                                padding: '0.5rem',
                                background: 'rgba(59, 130, 246, 0.05)',
                                borderRadius: '6px',
                                border: '1px solid rgba(59, 130, 246, 0.1)'
                              }}>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em'
                                }}>
                                  Purchase ID
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: '#3b82f6',
                                  fontWeight: '700',
                                  fontFamily: 'monospace',
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px'
                                }}>
                                  #{purchase.id}
                                </div>
                              </div>

                              {/* Details Row */}
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '1rem',
                                marginBottom: '0.75rem'
                              }}>
                                <div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '0.25rem'
                                  }}>
                                    Total Amount
                                  </div>
                                  <div style={{
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    color: '#1f2937'
                                  }}>
                                    ₹{purchase.total_amount?.toFixed(2) || '0.00'}
                                  </div>
                                </div>
                                <div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    marginBottom: '0.25rem'
                                  }}>
                                    Payment Mode
                                  </div>
                                  <div style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'capitalize'
                                  }}>
                                    {purchase.payment_mode || 'N/A'}
                                  </div>
                                </div>
                              </div>

                              {/* Status and Amount Row */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '0.75rem',
                                borderTop: '1px solid rgba(226, 232, 240, 0.5)'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  gap: '0.5rem',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    fontSize: '0.8rem',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '6px',
                                    backgroundColor: purchase.status === 'received' ? '#ecfdf5' : 
                                                    purchase.status === 'pending' ? '#fef2f2' : 
                                                    purchase.status === 'paid' ? '#ecfdf5' : '#fffbeb',
                                    color: purchase.status === 'received' ? '#10b981' : 
                                           purchase.status === 'pending' ? '#ef4444' : 
                                           purchase.status === 'paid' ? '#10b981' : '#f59e0b',
                                    fontWeight: '600',
                                    textTransform: 'capitalize'
                                  }}>
                                    {purchase.status || 'Unknown'}
                                  </div>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  gap: '0.5rem',
                                  alignItems: 'center'
                                }}>
                                  {purchase.settled_amount > 0 && (
                                    <div style={{
                                      fontSize: '0.75rem',
                                      color: '#10b981',
                                      fontWeight: '600'
                                    }}>
                                      Paid: ₹{purchase.settled_amount.toFixed(2)}
                                    </div>
                                  )}
                                  {purchase.unsettled_amount > 0 && (
                                    <div style={{
                                      fontSize: '0.75rem',
                                      color: '#ef4444',
                                      fontWeight: '600'
                                    }}>
                                      Due: ₹{purchase.unsettled_amount.toFixed(2)}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* View Medications Button */}
                              <div style={{
                                marginTop: '0.75rem',
                                display: 'flex',
                                justifyContent: 'center'
                              }}>
                                <button
                                  onClick={() => handleViewMedications(purchase.id)}
                                  style={{
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 12l2 2 4-4"/>
                                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                    <path d="M12 2a10 10 0 0 1 10 10"/>
                                  </svg>
                                  {expandedPurchaseId === purchase.id ? 'Hide Medications' : 'View Medications'}
                                </button>
                              </div>

                              {/* Created Date */}
                              <div style={{
                                fontSize: '0.7rem',
                                color: '#9ca3af',
                                marginTop: '0.5rem',
                                textAlign: 'right'
                              }}>
                                Created: {new Date(purchase.created_at).toLocaleDateString()} {new Date(purchase.created_at).toLocaleTimeString()}
                              </div>

                              {/* Medications Section */}
                              {expandedPurchaseId === purchase.id && (
                                <div style={{
                                  marginTop: '1rem',
                                  padding: '1rem',
                                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(59, 130, 246, 0.1)',
                                  animation: 'slideInUp 0.3s ease-out'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '1rem'
                                  }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                      <path d="M9 12l2 2 4-4"/>
                                      <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                    </svg>
                                    <h5 style={{
                                      fontSize: '1rem',
                                      fontWeight: '700',
                                      color: '#1f2937',
                                      margin: 0
                                    }}>
                                      Medications Details
                                    </h5>
                                  </div>

                                  {/* Loading State for Medications */}
                                  {isLoadingMedications && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '1.5rem',
                                      color: '#6b7280'
                                    }}>
                                      <div style={{
                                        width: '25px',
                                        height: '25px',
                                        border: '2px solid #e5e7eb',
                                        borderTop: '2px solid #3b82f6',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto 0.75rem auto'
                                      }}></div>
                                      <p style={{
                                        fontSize: '0.85rem',
                                        color: '#6b7280',
                                        margin: 0
                                      }}>
                                        Loading medications...
                                      </p>
                                    </div>
                                  )}

                                  {/* Error State for Medications */}
                                  {medicationsError && !isLoadingMedications && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '1.5rem',
                                      color: '#6b7280'
                                    }}>
                                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="15" y1="9" x2="9" y2="15"/>
                                        <line x1="9" y1="9" x2="15" y2="15"/>
                                      </svg>
                                      <p style={{
                                        fontSize: '0.85rem',
                                        color: '#6b7280',
                                        margin: 0
                                      }}>
                                        {medicationsError}
                                      </p>
                                    </div>
                                  )}

                                  {/* Medications Data Display */}
                                  {!isLoadingMedications && !medicationsError && purchaseMedications && (
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '1rem'
                                    }}>
                                      {/* Purchase Summary */}
                                      <div style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(59, 130, 246, 0.1)',
                                        marginBottom: '0.5rem'
                                      }}>
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                          gap: '1rem',
                                          fontSize: '0.85rem'
                                        }}>
                                          <div>
                                            <span style={{ fontWeight: '600', color: '#6b7280' }}>Purchase ID:</span>
                                            <span style={{ fontWeight: '700', color: '#3b82f6', marginLeft: '0.5rem' }}>#{purchaseMedications.id}</span>
                                          </div>
                                          <div>
                                            <span style={{ fontWeight: '600', color: '#6b7280' }}>Invoice:</span>
                                            <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>{purchaseMedications.invoice_no}</span>
                                          </div>
                                          <div>
                                            <span style={{ fontWeight: '600', color: '#6b7280' }}>Date:</span>
                                            <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>{purchaseMedications.invoice_date}</span>
                                          </div>
                                          <div>
                                            <span style={{ fontWeight: '600', color: '#6b7280' }}>Created:</span>
                                            <span style={{ fontWeight: '500', color: '#374151', marginLeft: '0.5rem' }}>
                                              {new Date(purchaseMedications.created_at).toLocaleDateString()}
                                            </span>
                                          </div>
                                        </div>
                                        
                                      </div>

                                      {/* Medications List */}
                                      {purchaseMedications.items && purchaseMedications.items.length > 0 && (
                                        <div>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            marginBottom: '1rem'
                                          }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#3b82f6' }}>
                                              <path d="M9 12l2 2 4-4"/>
                                              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                            </svg>
                                            <h6 style={{
                                              fontSize: '0.9rem',
                                              fontWeight: '700',
                                              color: '#1f2937',
                                              margin: 0
                                            }}>
                                              Medications ({purchaseMedications.items.length} items)
                                            </h6>
                                          </div>
                                          
                                          <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                          }}>
                                            {purchaseMedications.items.map((item: any, index: number) => (
                                              <div key={index} style={{
                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                                padding: '1rem',
                                                borderRadius: '10px',
                                                border: '1px solid rgba(226, 232, 240, 0.5)',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                                transition: 'all 0.2s ease',
                                                position: 'relative',
                                                overflow: 'hidden'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                                                e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.5)';
                                              }}>
                                                {/* Item Number Badge */}
                                                <div style={{
                                                  position: 'absolute',
                                                  top: '0.75rem',
                                                  right: '0.75rem',
                                                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                  color: 'white',
                                                  padding: '0.25rem 0.5rem',
                                                  borderRadius: '12px',
                                                  fontSize: '0.7rem',
                                                  fontWeight: '600',
                                                  fontFamily: 'monospace'
                                                }}>
                                                  #{index + 1}
                                                </div>

                                                {/* Medicine Name */}
                                                <div style={{
                                                  fontSize: '1rem',
                                                  fontWeight: '700',
                                                  color: '#1f2937',
                                                  marginBottom: '0.75rem',
                                                  paddingRight: '3rem'
                                                }}>
                                                  {item.medicine_name || 'Unknown Medicine'}
                                                </div>

                                                {/* Medicine Details Grid */}
                                                <div style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                                  gap: '0.75rem',
                                                  marginBottom: '0.75rem'
                                                }}>
                                                  <div style={{
                                                    background: 'rgba(16, 185, 129, 0.05)',
                                                    padding: '0.5rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(16, 185, 129, 0.1)'
                                                  }}>
                                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>QUANTITY</div>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#10b981' }}>{item.quantity || 'N/A'}</div>
                                                  </div>
                                                  
                                                  <div style={{
                                                    background: 'rgba(59, 130, 246, 0.05)',
                                                    padding: '0.5rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(59, 130, 246, 0.1)'
                                                  }}>
                                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>COST PRICE</div>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#3b82f6' }}>₹{item.cost_price_per_unit || '0.00'}</div>
                                                  </div>
                                                  
                                                  <div style={{
                                                    background: 'rgba(245, 158, 11, 0.05)',
                                                    padding: '0.5rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(245, 158, 11, 0.1)'
                                                  }}>
                                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>MRP</div>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#f59e0b' }}>₹{item.mrp || '0.00'}</div>
                                                  </div>
                                                  
                                                  <div style={{
                                                    background: 'rgba(239, 68, 68, 0.05)',
                                                    padding: '0.5rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(239, 68, 68, 0.1)'
                                                  }}>
                                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', marginBottom: '0.25rem' }}>TOTAL</div>
                                                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ef4444' }}>₹{item.line_total || '0.00'}</div>
                                                  </div>
                                                </div>

                                                {/* Additional Details */}
                                                <div style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                                  gap: '0.5rem',
                                                  fontSize: '0.8rem',
                                                  color: '#6b7280'
                                                }}>
                                                  <div><strong>Batch:</strong> {item.batch_no || 'N/A'}</div>
                                                  <div><strong>Expiry:</strong> {item.expiry_date || 'N/A'}</div>
                                                  <div><strong>Tax:</strong> {item.tax_amount || '0.00'}%</div>
                                                  <div><strong>Company:</strong> {item.medicine_company || 'N/A'}</div>
                                                  <div><strong>Composition:</strong> {item.medicine_composition || 'N/A'}</div>
                                                  <div><strong>Location:</strong> {item.storage_location || 'N/A'}</div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* No Medications Found */}
                                  {!isLoadingMedications && !medicationsError && !purchaseMedications && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '1.5rem',
                                      color: '#6b7280'
                                    }}>
                                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '0.75rem', opacity: 0.5 }}>
                                        <path d="M9 12l2 2 4-4"/>
                                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                                      </svg>
                                      <p style={{
                                        fontSize: '0.85rem',
                                        color: '#6b7280',
                                        margin: 0
                                      }}>
                                        No medication details found for this purchase
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Pagination Info */}
                      {!isLoadingPurchases && !purchasesError && purchasesPagination && (
                        <div style={{
                          marginTop: '1rem',
                          padding: '0.75rem',
                          background: 'rgba(16, 185, 129, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(16, 185, 129, 0.1)',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#6b7280',
                            fontWeight: '500'
                          }}>
                            Showing {distributorPurchases.length} of {purchasesPagination.total} purchases
                            {purchasesPagination.pages > 1 && (
                              <span> • Page {purchasesPagination.current_page} of {purchasesPagination.pages}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No Purchases Found */}
                      {!isLoadingPurchases && !purchasesError && distributorPurchases.length === 0 && (
                        <div style={{
                          textAlign: 'center',
                          padding: '2rem',
                          color: '#6b7280'
                        }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1rem', opacity: 0.5 }}>
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                          </svg>
                          <p style={{
                            fontSize: '0.9rem',
                            color: '#6b7280',
                            margin: 0
                          }}>
                            No purchase history found for this distributor
                          </p>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default DistributorHistoryTable;

